import React, { useState, useMemo } from 'react';
import HeaderFirst from 'utils/HeaderFirst';
import CustomBootstrapTable from 'utils/reactTable';
import { useNavigate, useParams } from 'react-router-dom';
import '../ventes/client.css';
import TablePaginators from '../ventes/TablePaginator';
import { FiPaperclip } from 'react-icons/fi';
import { PAIEMENT_CREANCE } from 'routes/navigation/navigationPaths';
import HeaderButton from 'utils/headerButton';
import useCreancesData from './useCreancesData';
import FilterableHeader from './FilterableHeader';
import TableLoader from 'components/Loader/TableLoader';
import { FaRedo } from 'react-icons/fa';
import { useDossierRole } from 'contexts/DossierRoleContext';
// import { toast } from 'react-toastify';

const CreancePage = () => {
  const { canEdit } = useDossierRole();
  //user connected
  const navigate = useNavigate();
  const { clientID: dossier_id } = useParams();
  const { updatedCreances, totalCreances, isLoading } = useCreancesData(dossier_id);
  const donnees = updatedCreances;
  const [selectedRows, setSelectedRows] = useState([]);
  const [serachTerm, setSearchedTerm] = useState('');
  const [filters, setFilters] = useState({
    clients: [],
    dateStart: '',
    dateEnd: '',
    amountRange: [0, 1000000],
    minAmount: 0,
    maxAmount: 1000000,
  });

  const handleRowSelect = (row, isSelect) => {
    if (isSelect) {
      setSelectedRows((prev) => [...prev, row.id]);
    } else {
      setSelectedRows((prev) => prev.filter((id) => id !== row.id));
    }
  };

  const handleSearchChange = (newSearchTerm) => {
    setSearchedTerm(newSearchTerm);
  };

  // Calculate filter options
  const filterOptions = useMemo(() => {
    const clients = new Set();
    let maxAmount = 1000000;

    donnees.forEach(creance => {
      const clientName = creance.client_details?.nom;
      if (clientName) clients.add(clientName);
      
      const amount = creance.montant_total || 0;
      if (amount > maxAmount) maxAmount = amount;
    });

    return {
      clients: Array.from(clients).sort((a, b) => a.localeCompare(b)).map(c => ({ value: c, label: c })),
      maxAmount: Math.max(1000000, Math.ceil(maxAmount))
    };
  }, [donnees]);

  

  const columns = [
    {
      dataField: 'id',
      text: 'N°',
      formatter: (cell, row) => (
        <div className="styleColonne" onClick={() => handleRowSelect(row)}>
          {cell}
        </div>
      ),
    },
    {
      dataField: 'client_details',
      text: 'Client',
      headerFormatter: (col) => (
        <FilterableHeader
          column={col}
          type="text"
          options={filterOptions.clients}
          currentValue={filters?.clients}
          onFilterChange={(val) => setFilters({ ...filters, clients: val })}
        />
      ),
      formatter: (cell, row) => (
        <div className="styleColonne" onClick={() => handleRowSelect(row)}>
          {cell?.nom}
        </div>
      ),
    },
    {
      dataField: 'updated_at',
      text: 'Date',
      headerFormatter: (col) => (
        <FilterableHeader
          column={col}
          type="date"
          currentValue={{ start: filters?.dateStart, end: filters?.dateEnd }}
          onFilterChange={(val) => setFilters({ ...filters, dateStart: val.start, dateEnd: val.end })}
        />
      ),
      formatter: (cell, row) => {
        const date = new Date(cell);
        const day = date.getDate().toString().padStart(2, '0');
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const year = date.getFullYear();
        const hours = date.getHours().toString().padStart(2, '0');
        const minutes = date.getMinutes().toString().padStart(2, '0');
        const formattedDate = `${day}/${month}/${year} à ${hours}:${minutes}`;
        return <div onClick={() => handleRowSelect(row)}>{formattedDate}</div>;
      },
    },
    {
      dataField: 'montant_total',
      text: 'Montant total',
      headerFormatter: (col) => (
        <FilterableHeader
          column={col}
          type="amount"
          min={0}
          max={filterOptions.maxAmount}
          currentValue={filters?.amountRange ? filters.amountRange : [0, Math.min(1000000, filterOptions.maxAmount)]}
          onFilterChange={(val) => setFilters({ ...filters, minAmount: val[0], maxAmount: val[1], amountRange: val })}
        />
      ),
      formatter: (cell, row) => {
        const raw = cell ?? row?.montant_total ?? row?.prix ?? 0;
        const num = Number(raw);
        const safeAmount = Number.isFinite(num) ? num : 0;
        return (
          <div className="styleColonne" onClick={() => handleRowSelect(row)}>
            {new Intl.NumberFormat('fr-FR').format(safeAmount)} FCFA
          </div>
        );
      },
    },
    {
      dataField: 'montant_paye',
      text: 'Montant payé',
      formatter: (cell, row) => {
        const raw = cell ?? row?.montant_paye ?? 0;
        const num = Number(raw);
        const safeAmount = Number.isFinite(num) ? num : 0;
        return (
          <div className="styleColonne" onClick={() => handleRowSelect(row)}>
            {new Intl.NumberFormat('fr-FR').format(safeAmount)} FCFA
          </div>
        );
      },
    },
    {
      dataField: 'reste_a_payer',
      text: 'Solde restant',
      formatter: (cell, row) => {
        const raw = cell ?? row?.reste_a_payer ?? row?.montant_restant ?? 0;
        const num = Number(raw);
        const safeAmount = Number.isFinite(num) ? num : 0;
        return (
          <div className="styleColonne" onClick={() => handleRowSelect(row)}>
            <span style={{ color: safeAmount > 0 ? '#d9534f' : '#28a745', fontWeight: 'bold' }}>
              {new Intl.NumberFormat('fr-FR').format(safeAmount)} FCFA
            </span>
          </div>
        );
      },
    },
    {
      dataField: 'archived',
      text: 'Statut',
      formatter: (cell, row) => {
        const statusText = row.archived ? (
          <span style={{ color: 'red' }}>Archivée</span>
        ) : (
          'Active'
        );
        return <div onClick={() => handleRowSelect(row)}>{statusText}</div>;
      },
    },
    {
      dataField: 'piece_joint',
      text: <FiPaperclip />,
      headerFormatter: () => (
        <div className="d-flex align-items-center justify-content-between">
          <span><FiPaperclip /></span>
          <FaRedo
            title="Réinitialiser tous les filtres"
            className="text-muted cursor-pointer ms-2"
            style={{ cursor: 'pointer', fontSize: '1rem' }}
            onClick={(e) => {
              e.stopPropagation();
              setFilters({
                clients: [],
                dateStart: '',
                dateEnd: '',
                amountRange: [0, filterOptions.maxAmount],
                minAmount: 0,
                maxAmount: filterOptions.maxAmount
              });
            }}
          />
        </div>
      ),
      formatter: (cell, row) => (
        <div className="styleColonne" onClick={() => handleRowSelect(row)}>
          {cell ? (
            <a href={cell} target="_blank" rel="noopener noreferrer">
              Pièce jointe
            </a>
          ) : (
            <span>-</span>
          )}
        </div>
      ),
    },
  ];

  const [pageSize, setPageSize] = useState(10);
  const [paginationIndex, setPaginationIndex] = useState(0);
  const [viewArchived, setViewArchived] = useState(false);

  const handlePageChange = (newIndex) => {
    setPaginationIndex(newIndex);
  };

  const term = serachTerm ? serachTerm.trim().toLowerCase() : '';
  const filteredData = (donnees || [])
    .filter((creance) => (viewArchived ? creance.archived : !creance.archived))
    .filter((creance) => {
      if (!term) return true;
      const nomClient = (
        creance.client_details?.nom ||
        creance.client_nom ||
        ''
      ).toLowerCase();
      const intitule = (creance.intitule || '').toLowerCase();
      const montant = String(creance.montant_total || creance.prix || creance.reste_a_payer || '');
      return (
        nomClient.includes(term) ||
        intitule.includes(term) ||
        montant.includes(term)
      );
    })
    .filter((creance) => {
      if (!filters.clients || filters.clients.length === 0) return true;
      return filters.clients.includes(creance.client_details?.nom);
    })
    .filter((creance) => {
      if (!filters.dateStart && !filters.dateEnd) return true;
      const creanceDate = new Date(creance.updated_at);
      if (filters.dateStart) {
        const dateStart = new Date(filters.dateStart);
        if (creanceDate < dateStart) return false;
      }
      if (filters.dateEnd) {
        const dateEnd = new Date(filters.dateEnd);
        dateEnd.setHours(23, 59, 59, 999);
        if (creanceDate > dateEnd) return false;
      }
      return true;
    })
    .filter((creance) => {
      if (!filters.amountRange) return true;
      const amount = creance.montant_total || 0;
      return amount >= filters.amountRange[0] && amount <= filters.amountRange[1];
    });

  const currentPageData = filteredData.slice(
    paginationIndex * pageSize,
    (paginationIndex + 1) * pageSize
  );

  // Define the action handler for TablePaginator
  const handleActionSelected = (action) => {
    if (action === 'archive') {
      setViewArchived(!viewArchived); // Toggle between viewing archived and active depenses
    } else {
      console.log(`Action selected: ${action}`);
      // Handle other actions as necessary
    }
  };

  // const onClickArchive = async () => {
  //   if (selectedRows.length === 1) {
  //     const selectedRowId = selectedRows[0];

  //     // Optional: Check if the creance is archived before attempting to update
  //     const creance = currentPageData.find((d) => d.id === selectedRowId);

  //     const newArchivedStatus = !creance.archived;
  //     await handleUpdateCreance(selectedRowId, { archived: newArchivedStatus });
  //     toast.info(
  //       `Créance ${newArchivedStatus ? 'archivée' : 'désarchivée'} avec succès.`
  //     );
  //     // Consider refetching updated data here to reflect changes
  //   } else {
  //     toast.warning('Veuillez sélectionner exactement une créance à archiver.');
  //   }
  // };

  // const DepensebuttonActions = {
  //   addExpense: () => {
  //     alert('Ajouter une action de créance');
  //   },
  // };

  const nom = (
    <>
    <div style={{ display: 'flex' }}>
      <h4 className="titre-dossier">{viewArchived ? 'Créances archivées' : 'Créances'}</h4>
      <span className="nbrDossier">{filteredData?.length}</span>
    </div>      
      <p className="fw-bold fs-5" style={{ color: "#991B1B" }}>
        <span className='fw-bold nbrDossier m-0'>Montant total des créances : </span> {new Intl.NumberFormat('fr-FR').format(totalCreances)} FCFA
    </p>
    
    </>
  );
  const description = (
    <div>Voici toutes vos créances {viewArchived && 'archivées'}</div>
  );

  const handleEyesClick = () => {
    if (selectedRows.length === 1) {
      const selectedRowId = selectedRows[0];
      const detailCreanceURL = `${selectedRowId}/`;
      navigate(detailCreanceURL);
    }
  };

  const handleFormCreance = () => {
    navigate(PAIEMENT_CREANCE);
  };

  // Vérifier s'il y a des créances avec solde restant
  const hasCreancesWithBalance = donnees.some(
    (creance) => creance.reste_a_payer > 0 && !creance.archived
  );

  return (
    <div
      className="global"
      style={{
        backgroundColor: 'white',
        minHeight: '500px',
        borderRadius: '20px',
        padding: '15px',
      }}
    >
      <div
        className="header pb-3"
        style={{
          backgroundColor: 'white',
          borderRadius: '10px',
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          flexWrap: 'wrap',
          // padding: "13px",
        }}
      >
        <HeaderFirst nom={nom} description={description} />
        {canEdit && (
        <button
          type="button"
          className="btn btn-outline-dark"
          style={{
            borderRadius: '31px',
            border: '1px solid #AEAEAE',
            padding: '8px',
          }}
          onClick={handleFormCreance}
          disabled={!hasCreancesWithBalance}
          title={!hasCreancesWithBalance ? 'Aucune créance à payer' : ''}
        >
          <span style={ajoutFou}>+ Paiement du client</span>
        </button>
        )}
      </div>
      <HeaderButton
        isButtonsEnabled={selectedRows.length > 0}
        placeholder={'Rechercher une créance'}
        onSearchChange={handleSearchChange}
        onEyesClick={handleEyesClick}
        // buttonActions={DepensebuttonActions}
        // onClickArchive={onClickArchive}
        // onEditClick={onEditClick}
      />

      <div>
        {isLoading && (!donnees || donnees.length === 0) ? (
          <div style={{ background: '#ffffff', borderRadius: '12px', padding: '30px', border: '1px solid #e2eced' }}>
            <TableLoader
              message="Chargement des créances clients..."
              subMessage="Calcul des montants dus, des restes à payer et des échéances"
              minHeight="280px"
            />
          </div>
        ) : donnees ? (
          <CustomBootstrapTable
            keyField="id"
            data={currentPageData}
            columns={columns}
            onSelectRow={handleRowSelect}
            // ... other props
          />
        ) : null}
      </div>
      {!isLoading && (!currentPageData || currentPageData.length === 0) && (
        <div className="d-flex justify-content-center fs-5 mt-4 text-muted">
          <p>Aucune créance à afficher.</p>
        </div>
      )}
      {/* pagination */}
      {currentPageData && (
        <TablePaginators
          paginationIndex={paginationIndex}
          setPaginationIndex={handlePageChange}
          dataLength={filteredData?.length}
          pageSize={pageSize}
          setPageSize={setPageSize}
          onActionSelected={handleActionSelected}
          isViewingArchived={viewArchived}
        />
      )}
    </div>
  );
};

const ajoutFou = {
  fontFamily: 'Montserrat',
  fontWeight: '600',
  fontSize: '13px',
};
export default CreancePage;
